
import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import BookingForm from "@/components/BookingForm";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Calendar,
  Clock,
  Feather,
  Heart,
  Home,
  MapPin,
  ShoppingBasket,
  Smile,
  Star,
  Sun,
  Utensils,
  X
} from "lucide-react";

interface Service {
  id: number;
  icon: React.ReactNode;
  title: string;
  description: string;
  price: string;
  duration: string;
  popular?: boolean;
}

const Services: React.FC = () => {
  const navigate = useNavigate();
  const [selectedLocation, setSelectedLocation] = useState("");
  const [searchRadius, setSearchRadius] = useState(10);
  const [selectedDate, setSelectedDate] = useState("");
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  
  const services: Service[] = [
    {
      id: 1,
      icon: <Home className="h-5 w-5" />,
      title: "Home Care",
      description: "Personal assistance with daily activities, mobility, and comfort care.",
      price: "€25/hr",
      duration: "1-4 hours",
      popular: true
    },
    {
      id: 2,
      icon: <Feather className="h-5 w-5" />,
      title: "House Cleaning",
      description: "Professional cleaning of living areas, kitchen, bathroom, and more.",
      price: "€30/hr",
      duration: "2-3 hours"
    },
    {
      id: 3,
      icon: <ShoppingBasket className="h-5 w-5" />,
      title: "Grocery Shopping",
      description: "Shop and deliver groceries directly to the home.",
      price: "€22/hr",
      duration: "1-2 hours"
    },
    {
      id: 4,
      icon: <Sun className="h-5 w-5" />,
      title: "Garden Maintenance",
      description: "Lawn mowing, plant care, and general garden upkeep.",
      price: "€28/hr",
      duration: "2-4 hours"
    },
    {
      id: 5,
      icon: <Smile className="h-5 w-5" />,
      title: "Companionship",
      description: "Social visits, conversation, and recreational activities.",
      price: "€20/hr",
      duration: "1-3 hours"
    },
    {
      id: 6,
      icon: <Utensils className="h-5 w-5" />,
      title: "Meal Preparation",
      description: "Fresh meal cooking and preparation for several days.",
      price: "€24/hr",
      duration: "2-3 hours"
    }
  ];

  const handleBookNow = (service: Service) => {
    setSelectedService(service);
    setShowBookingForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  return (
    <Layout>
      <Helmet>
        <title>Services | CareConnect</title>
        <meta name="description" content="Book home care services for your elderly loved ones including cleaning, personal assistance, grocery shopping, and more." />
      </Helmet>
      
      {/* Services Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-care-50 to-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block py-1 px-3 rounded-full text-sm font-medium bg-care-100 text-care-800 mb-4">
                Book With Ease
              </span>
              
              <h1 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight text-balance">
                Find the perfect <span className="title-gradient">home care</span> services
              </h1>
              
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto text-balance">
                Browse and book from our variety of professional services designed to support elderly individuals in the comfort of their own homes.
              </p>
            </motion.div>
            
            {showBookingForm ? (
              <motion.div
                className="glass-morphism rounded-xl p-6 mt-8"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-semibold text-care-800">
                    Book {selectedService?.title}
                  </h2>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowBookingForm(false)}
                    className="text-muted-foreground"
                  >
                    <X className="h-5 w-5" /> Close
                  </Button>
                </div>
                <BookingForm 
                  preselectedService={selectedService?.id.toString()} 
                  serviceName={selectedService?.title} 
                />
              </motion.div>
            ) : (
              <motion.div
                className="glass-morphism rounded-xl p-6 mt-8"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-care-500 h-5 w-5" />
                    <input
                      type="text"
                      placeholder="Enter location"
                      className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-care-500"
                      value={selectedLocation}
                      onChange={(e) => setSelectedLocation(e.target.value)}
                    />
                    {selectedLocation && (
                      <button 
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        onClick={() => setSelectedLocation("")}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-care-500 h-5 w-5" />
                    <input
                      type="date"
                      className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-care-500"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                    />
                  </div>
                  
                  <Button className="btn-primary w-full">
                    Find Services
                  </Button>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>
      
      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold mb-4">Available Services</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Our carefully vetted professionals provide a range of services to help elderly individuals maintain their independence and quality of life.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                className={`service-card relative ${service.popular ? 'border-care-200' : ''}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                {service.popular && (
                  <span className="absolute -top-3 right-3 bg-care-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    Popular
                  </span>
                )}
                
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-care-50 rounded-lg text-care-600 mr-4">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-care-800">{service.title}</h3>
                </div>
                
                <p className="text-muted-foreground mb-4 text-balance">
                  {service.description}
                </p>
                
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center text-care-800">
                    <span className="font-semibold">{service.price}</span>
                  </div>
                  <div className="flex items-center text-muted-foreground text-sm">
                    <Clock className="h-4 w-4 mr-1" />
                    <span>{service.duration}</span>
                  </div>
                </div>
                
                <Button 
                  className="w-full bg-care-500 hover:bg-care-600 text-white"
                  onClick={() => handleBookNow(service)}
                >
                  Book Now
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* How Booking Works - Round Robin Section (Fixed Padding) */}
      <section className="py-20 bg-care-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <span className="inline-block py-1 px-3 rounded-full text-sm font-medium bg-care-100 text-care-800 mb-4">
              Round-Robin System
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-6 tracking-tight text-balance">
              How our booking system works
            </h2>
            <p className="text-muted-foreground text-lg text-balance">
              We use a fair, efficient round-robin system to assign service requests to available professionals in your area.
            </p>
          </div>
          
          <div className="relative max-w-5xl mx-auto px-4">
            {/* Timeline Line */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-care-200 transform -translate-x-1/2" />
            
            {/* Timeline Steps */}
            <div className="space-y-24">
              {/* Step 1 */}
              <div className="relative">
                <motion.div
                  className="md:flex items-center"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="md:w-1/2 mb-8 md:mb-0 md:pr-16 md:text-right">
                    <h3 className="text-2xl font-semibold mb-4 text-care-800">
                      Request Submission
                    </h3>
                    <p className="text-muted-foreground text-lg">
                      You submit a service request with your specific requirements, location, and preferred time.
                    </p>
                  </div>
                  
                  <div className="hidden md:flex absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white border-4 border-care-200 rounded-full h-14 w-14 items-center justify-center z-10">
                    <span className="text-care-800 text-xl font-semibold">1</span>
                  </div>
                  
                  <div className="md:w-1/2 md:pl-16">
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                      <div className="flex items-start">
                        <Calendar className="h-7 w-7 text-care-500 mr-4 mt-1 flex-shrink-0" />
                        <div>
                          <span className="block font-medium text-base mb-1">Home Cleaning</span>
                          <span className="text-sm text-muted-foreground">May 15, 2023 at 9:00 AM</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
              
              {/* Step 2 */}
              <div className="relative">
                <motion.div
                  className="md:flex flex-col md:flex-row"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="md:w-1/2 md:pr-16 md:text-right">
                    <h3 className="text-2xl font-semibold mb-4 text-care-800">
                      Local Provider Matching
                    </h3>
                    <p className="text-muted-foreground text-lg mb-8 md:mb-0">
                      Our system identifies qualified professionals in your area who are available at your requested time.
                    </p>
                  </div>
                  
                  <div className="hidden md:flex absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white border-4 border-care-200 rounded-full h-14 w-14 items-center justify-center z-10">
                    <span className="text-care-800 text-xl font-semibold">2</span>
                  </div>
                  
                  <div className="md:w-1/2 md:pl-16">
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                      <div className="flex items-center">
                        <div className="text-left">
                          <span className="block font-medium text-base mb-1">5 Available Caregivers</span>
                          <span className="text-sm text-muted-foreground">Within 10 miles of your location</span>
                        </div>
                        <MapPin className="h-7 w-7 text-care-500 ml-4 flex-shrink-0" />
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
              
              {/* Step 3 */}
              <div className="relative">
                <motion.div
                  className="md:flex flex-col md:flex-row"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="md:w-1/2 mb-8 md:mb-0 md:pr-16 md:text-right">
                    <h3 className="text-2xl font-semibold mb-4 text-care-800">
                      First Responder Assignment
                    </h3>
                    <p className="text-muted-foreground text-lg">
                      The request is sent to all available providers, and the first one to accept gets the assignment.
                    </p>
                  </div>
                  
                  <div className="hidden md:flex absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white border-4 border-care-200 rounded-full h-14 w-14 items-center justify-center z-10">
                    <span className="text-care-800 text-xl font-semibold">3</span>
                  </div>
                  
                  <div className="md:w-1/2 md:pl-16">
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                      <div className="flex items-center">
                        <div className="h-12 w-12 rounded-full bg-care-100 flex items-center justify-center mr-4 flex-shrink-0">
                          <Heart className="h-6 w-6 text-care-500" />
                        </div>
                        <div>
                          <span className="block font-medium text-base mb-1">Sarah Johnson</span>
                          <span className="text-sm text-green-600">Accepted your request</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
              
              {/* Step 4 */}
              <div className="relative">
                <motion.div
                  className="md:flex flex-col md:flex-row"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="md:w-1/2 md:pr-16 md:text-right">
                    <h3 className="text-2xl font-semibold mb-4 text-care-800">
                      Service & Rating
                    </h3>
                    <p className="text-muted-foreground text-lg mb-8 md:mb-0">
                      Your service is delivered at the scheduled time. Afterward, you can rate your experience and provide feedback.
                    </p>
                  </div>
                  
                  <div className="hidden md:flex absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white border-4 border-care-200 rounded-full h-14 w-14 items-center justify-center z-10">
                    <span className="text-care-800 text-xl font-semibold">4</span>
                  </div>
                  
                  <div className="md:w-1/2 md:pl-16">
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                      <div className="flex items-center">
                        <div className="text-left mr-4">
                          <span className="block font-medium text-base mb-1">Service Completed</span>
                          <span className="text-sm text-muted-foreground">Rate your experience</span>
                        </div>
                        <div className="flex text-yellow-400 flex-shrink-0">
                          <Star className="h-5 w-5" />
                          <Star className="h-5 w-5" />
                          <Star className="h-5 w-5" />
                          <Star className="h-5 w-5" />
                          <Star className="h-5 w-5" />
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-white border-t border-gray-100">
        <div className="container-custom">
          <div className="glass-morphism rounded-2xl p-10 md:p-16 bg-gradient-to-br from-care-50 to-white border border-care-100 overflow-hidden relative">
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-care-100 rounded-full opacity-50" />
            <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-care-200 rounded-full opacity-30" />
            
            <div className="relative z-10 max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-care-800 text-balance">
                Start providing better care today
              </h2>
              <p className="text-lg text-muted-foreground mb-10 text-balance">
                Book your first service now and experience the peace of mind that comes with knowing your loved ones are in good hands.
              </p>
              <Button className="btn-primary text-base px-8 py-4">
                Book Your First Service
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Services;
